import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AssigneePicker,
  type AssignmentDraft,
  NO_ASSIGNMENT,
  useSetAssignment,
} from '@/entities/collab';
import { useNavigate } from 'react-router';

import { currentTheme } from '@/shared/lib/theme';
import { type Look, NEUTRAL_SURFACE, cardBackdrops, STANDARD_LOOK } from '@/entities/appearance';
import {
  type CardCreateInput,
  displayColumnName,
  LabelChipPicker,
  useCreateCard,
  useDestination,
} from '@/entities/board';
import {
  defaultDraft,
  RecurrenceFields,
  type RecurrenceDraft,
  type RecurrenceError,
  draftToInput,
  validateRecurrence,
} from '@/entities/recurrence';
import { useQuickCreateDefaults } from '@/entities/workspace';
import {
  isLookValid,
  LookEditor,
  LookPreview,
  lookPayload,
  TextColors,
  withTextColor,
} from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import {
  type DeadlineDraft,
  type DeadlineErrorCode,
  rangeKind,
  rangeToPayload,
  validateRange,
} from '@/shared/lib/deadline-draft';
import {
  Button,
  Combobox,
  ConfirmDialog,
  DateProperty,
  HelpRow,
  Dialog,
  ErrorState,
  LoadingState,
  useToast,
} from '@/shared/ui';

import styles from './AdvancedCreate.module.css';
import { blocksPayload, type DraftBlock } from './draft';
import { DraftContent } from './DraftContent';

const CARD_TITLE_MAX = 500;
const EMPTY_DATES: DeadlineDraft = {
  hasStart: false,
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
};
const CARD_STYLES = ['header', 'border', 'accent', 'soft', 'solid', 'gradient', 'pattern'] as const;

interface Props {
  /** `null`: a guest of this board alone (the board is then always fixed). */
  workspaceId: string | null;
  timezone: string | null;
  /** Create on this board only (the board page). */
  fixedBoardId?: string;
  onClose: () => void;
}

/**
 * "Create Advanced Task": the whole card is composed locally - title, destination, labels, a
 * deadline or period, appearance, Markdown and checklist blocks, a recurrence - and created
 * with ONE request when confirmed. Opening the dialog creates nothing on the server, cancelling
 * leaves nothing behind, and a failed creation keeps the draft and shows the error.
 *
 * Things that need a stored card (attachments, a cover image, history) are not offered here:
 * they are available in the card dialog right after creation.
 */
export function AdvancedCreateDialog({ workspaceId, timezone, fixedBoardId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const toast = useToast();
  const navigate = useNavigate();
  const defaults = useQuickCreateDefaults(
    workspaceId ?? '',
    workspaceId !== null && fixedBoardId === undefined,
  );
  const [boardChoice, setBoardChoice] = useState<string | null>(null);
  const [columnChoice, setColumnChoice] = useState<string | null>(null);
  const destination = useDestination({
    workspaceId,
    ...(fixedBoardId !== undefined && { fixedBoardId }),
    preferred: defaults.data
      ? { boardId: defaults.data.default_board_id, columnId: defaults.data.default_column_id }
      : null,
    boardChoice,
    columnChoice,
  });
  const { boardId, columnId, columns, boardList, detail } = destination;
  const create = useCreateCard(boardId);
  const assign = useSetAssignment(boardId);
  const [assignment, setAssignment] = useState<AssignmentDraft>(NO_ASSIGNMENT);
  const mayAssign = detail.data?.permissions.includes('task.assign') ?? false;

  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [labelChoice, setLabelChoice] = useState<string[]>([]);
  const [dates, setDates] = useState<DeadlineDraft>(EMPTY_DATES);
  const [dateErrors, setDateErrors] = useState<DeadlineErrorCode[]>([]);
  const [look, setLook] = useState<Look | null>(null);
  const [lookTouched, setLookTouched] = useState(false);
  const [showLook, setShowLook] = useState(false);
  const [blocks, setBlocks] = useState<DraftBlock[]>([]);
  const [repeat, setRepeat] = useState<RecurrenceDraft | null>(null);
  const [repeatErrors, setRepeatErrors] = useState<RecurrenceError[]>([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  // Only labels of the board being created on count (switching boards drops the others).
  const boardLabels = detail.data?.labels ?? [];
  const labelIds = labelChoice.filter((labelId) => boardLabels.some((l) => l.id === labelId));

  const dirty =
    title.trim() !== '' ||
    labelIds.length > 0 ||
    rangeKind(dates) !== 'none' ||
    lookTouched ||
    blocks.length > 0 ||
    repeat !== null;

  const requestClose = () => {
    if (dirty && !create.isPending) setConfirmDiscard(true);
    else onClose();
  };

  const submit = () => {
    const trimmed = title.trim();
    setTitleError(trimmed === '');
    const found = repeat ? validateRecurrence(repeat) : [];
    setRepeatErrors(found);
    const foundDates = validateRange(dates);
    setDateErrors(foundDates);
    if (
      trimmed === '' ||
      found.length > 0 ||
      foundDates.length > 0 ||
      !columnId ||
      !isLookValid(look)
    ) {
      return;
    }
    const deadline = rangeToPayload(dates);
    const payload: CardCreateInput = {
      columnId,
      title: trimmed,
      ...(deadline && { deadline }),
      ...(labelIds.length > 0 && { label_ids: labelIds }),
      // Only an appearance the user chose is sent; otherwise the server snapshots the column's.
      ...(lookTouched && { appearance: lookPayload(look) ?? STANDARD_LOOK }),
      ...(blocks.length > 0 && { content: blocksPayload(blocks) }),
      ...(repeat && { recurrence: draftToInput(repeat, timezone) }),
    };
    create.mutate(payload, {
      onSuccess: (card) => {
        if (mayAssign && assignment.kind !== 'unassigned') {
          assign.mutate({
            cardId: card.id,
            kind: assignment.kind,
            assigneeIds: assignment.kind === 'assigned' ? assignment.ids : [],
          });
        }
        toast.show({
          message: t('advancedCreate.created'),
          actionLabel: t('toast.open'),
          onAction: () => Promise.resolve(navigate(`/app/boards/${boardId}/cards/${card.id}`)),
        });
        onClose();
      },
    });
  };

  const neutral = NEUTRAL_SURFACE[currentTheme() === 'dark' ? 'dark' : 'light'];
  const backdrops = cardBackdrops(look, neutral);

  let body;
  if (destination.boards.isPending || (fixedBoardId === undefined && defaults.isPending)) {
    body = <LoadingState />;
  } else if (destination.boards.isError) body = <ErrorState error={destination.boards.error} />;
  else if (boardList.length === 0) body = <p>{t('quickCreate.noBoards')}</p>;
  else {
    body = (
      <form
        className={styles.form}
        noValidate
        data-testid="advanced-create"
        onSubmit={(event) => {
          event.preventDefault();
          if (event.target !== event.currentTarget) return;
          submit();
        }}
      >
        <div className={styles.columns}>
          <div className={styles.contentColumn}>
            <div className={styles.field}>
              <label htmlFor={`${id}-title`} className={styles.label}>
                {t('cards.title')}
              </label>
              <input
                id={`${id}-title`}
                ref={titleRef}
                className={styles.input}
                value={title}
                maxLength={CARD_TITLE_MAX}
                autoComplete="off"
                autoFocus
                aria-invalid={titleError}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleError(false);
                }}
              />
              {titleError && (
                <span role="alert" className={styles.error}>
                  {t('validation.REQUIRED')}
                </span>
              )}
            </div>

            <DraftContent blocks={blocks} onChange={setBlocks} />
            <p className={styles.hint}>{t('advancedCreate.afterCreate')}</p>
          </div>

          <div className={styles.propertiesColumn}>
            <div className={styles.destination}>
              {fixedBoardId === undefined && (
                <div className={styles.field}>
                  <label htmlFor={`${id}-board`} className={styles.label}>
                    {t('calendar.board')}
                  </label>
                  <Combobox
                    id={`${id}-board`}
                    label={t('calendar.board')}
                    value={boardId}
                    options={boardList.map((option) => ({ value: option.id, label: option.name }))}
                    onChange={(next) => {
                      setBoardChoice(next);
                      setAssignment(NO_ASSIGNMENT);
                      setColumnChoice(null);
                      setLabelChoice([]);
                    }}
                  />
                </div>
              )}
              <div className={styles.field}>
                <label htmlFor={`${id}-column`} className={styles.label}>
                  {t('calendar.column')}
                </label>
                <Combobox
                  id={`${id}-column`}
                  label={t('calendar.column')}
                  value={columnId}
                  disabled={detail.isPending || columns.length === 0}
                  options={columns.map((column) => ({
                    value: column.id,
                    label: displayColumnName(column, t('columns.calendar')),
                  }))}
                  onChange={setColumnChoice}
                />
              </div>
            </div>

            {mayAssign && (
              <AssigneePicker
                boardId={boardId}
                value={assignment}
                disabled={create.isPending}
                onChange={setAssignment}
              />
            )}

            {boardLabels.length > 0 && (
              <section className={styles.section} aria-labelledby={`${id}-labels`}>
                <HelpRow topic={t('labels.title')} topicId="labels" text={t('help.labels')}>
                  <h3 id={`${id}-labels`} className={styles.sectionTitle}>
                    {t('labels.title')}
                  </h3>
                </HelpRow>
                <LabelChipPicker
                  labels={boardLabels}
                  selectedIds={labelIds}
                  label={t('labels.title')}
                  emptyText={t('labels.pickerEmpty')}
                  onToggle={(label, selected) => {
                    setLabelChoice(
                      selected
                        ? [...labelIds.filter((x) => x !== label.id), label.id]
                        : labelIds.filter((x) => x !== label.id),
                    );
                  }}
                />
              </section>
            )}

            <DateProperty
              value={dates}
              onChange={(next) => {
                setDates(next);
                setDateErrors([]);
              }}
              errors={dateErrors}
              timezone={timezone}
            />

            <section className={styles.section} aria-labelledby={`${id}-repeat`}>
              <HelpRow
                topic={t('recurrence.title')}
                topicId="recurring"
                text={t('help.recurrence')}
              >
                <h3 id={`${id}-repeat`} className={styles.sectionTitle}>
                  {t('recurrence.title')}
                </h3>
              </HelpRow>
              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={repeat !== null}
                  onChange={(event) => {
                    setRepeat(
                      event.target.checked
                        ? defaultDraft(dates.startDate || dates.endDate || todayLocal(timezone))
                        : null,
                    );
                    setRepeatErrors([]);
                  }}
                />
                {t('recurrence.advancedRepeat')}
              </label>
              {repeat && (
                <RecurrenceFields
                  draft={repeat}
                  onChange={(next) => {
                    setRepeat(next);
                    setRepeatErrors([]);
                  }}
                  errors={repeatErrors}
                  timezone={timezone}
                  locale={i18n.language}
                />
              )}
            </section>

            <section className={styles.section} aria-labelledby={`${id}-look`}>
              <HelpRow topic={t('cardLook.title')} topicId="kanban" text={t('help.appearance')}>
                <h3 id={`${id}-look`} className={styles.sectionTitle}>
                  {t('cardLook.title')}
                </h3>
              </HelpRow>
              {showLook ? (
                <div className={styles.look}>
                  <LookPreview look={lookPayload(look)} />
                  <LookEditor
                    value={look}
                    onChange={(next) => {
                      setLook(next);
                      setLookTouched(true);
                    }}
                    allowed={CARD_STYLES}
                    idPrefix="advanced"
                  />
                  <TextColors
                    idPrefix="advanced"
                    fields={[
                      {
                        key: 'title_color',
                        label: t('appearance.text.fields.title'),
                        role: 'text',
                      },
                      { key: 'text_color', label: t('appearance.text.fields.text'), role: 'text' },
                      {
                        key: 'muted_color',
                        label: t('appearance.text.fields.muted'),
                        role: 'text',
                      },
                      {
                        key: 'surface',
                        label: t('appearance.text.fields.surface'),
                        role: 'surface',
                      },
                    ]}
                    values={{
                      title_color: look?.title_color,
                      text_color: look?.text_color,
                      muted_color: look?.muted_color,
                      surface: look?.surface,
                    }}
                    onChange={(key, value) => {
                      setLook((current) =>
                        withTextColor(
                          current,
                          key as 'title_color' | 'text_color' | 'muted_color' | 'surface',
                          value,
                        ),
                      );
                      setLookTouched(true);
                    }}
                    backdrops={() => backdrops}
                  />
                </div>
              ) : (
                <div className={styles.line}>
                  <span className={styles.hint}>{t('advancedCreate.lookDefault')}</span>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowLook(true);
                    }}
                  >
                    {t('advancedCreate.customizeLook')}
                  </Button>
                </div>
              )}
            </section>
          </div>
        </div>

        <FormError error={create.error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={create.isPending || !columnId}>
            {t('advancedCreate.submit')}
          </Button>
          <Button onClick={requestClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    );
  }

  return (
    <Dialog title={t('advancedCreate.title')} size="xl" onClose={requestClose}>
      {body}
      {confirmDiscard && (
        <ConfirmDialog
          title={t('advancedCreate.discardTitle')}
          message={t('advancedCreate.discardMessage')}
          confirmLabel={t('advancedCreate.discard')}
          onCancel={() => {
            setConfirmDiscard(false);
          }}
          onConfirm={onClose}
        />
      )}
    </Dialog>
  );
}

function todayLocal(timezone: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timezone && { timeZone: timezone }),
  }).format(new Date());
}
